import { useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, X, MessageCircle, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_PATH = process.env.PUBLIC_URL || "";
const API = `${BACKEND_URL || BASE_PATH}/api`;

export function CartDrawer({ open, onClose, cart, updateQuantity, removeFromCart, clearCart, contact }) {
  const [checkout, setCheckout] = useState({ customer_name: "", email: "", phone: "", city: "", message: "" });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const updateCheckout = (field, value) => setCheckout((current) => ({ ...current, [field]: value }));
  const cleanNumber = contact.whatsapp_number.replace(/\D/g, "") || "910000000000";

  const submitOrder = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...checkout,
        items: cart.map(({ slug, name, price, quantity }) => ({ slug, name, price, quantity })),
      };
      const response = await axios.post(`${API}/orders`, payload);
      setOrder(response.data);
    } catch (err) {
      setError("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappUrl = order ? `https://wa.me/${cleanNumber}?text=${order.whatsapp_message}` : "#";

  if (!open) return null;

  return (
    <div className="cart-layer" data-testid="cart-drawer-layer">
      <button className="cart-backdrop" onClick={onClose} aria-label="Close cart" data-testid="cart-backdrop-button" />
      <aside className="cart-drawer" aria-label="Shopping cart" data-testid="cart-drawer">
        <div className="cart-drawer__header">
          <div>
            <p data-testid="cart-eyebrow">Project Umeed Cart</p>
            <h2 data-testid="cart-title">Your conscious order</h2>
          </div>
          <Button className="icon-button" variant="ghost" size="icon" onClick={onClose} data-testid="cart-close-button">
            <X aria-hidden="true" />
          </Button>
        </div>
        {cart.length === 0 ? (
          <div className="cart-empty" data-testid="cart-empty-state">
            <ShoppingBag aria-hidden="true" />
            <p data-testid="cart-empty-message">Your cart is ready for handcrafted pieces.</p>
          </div>
        ) : (
          <>
            <div className="cart-items" data-testid="cart-items-list">
              {cart.map((item) => (
                <div className="cart-item" key={item.slug} data-testid={`cart-item-${item.slug}`}>
                  <div>
                    <h3 data-testid={`cart-item-name-${item.slug}`}>{item.name}</h3>
                    <p data-testid={`cart-item-price-${item.slug}`}>₹{item.price.toLocaleString("en-IN")}</p>
                  </div>
                  <div className="cart-item__controls">
                    <Button variant="outline" size="icon" onClick={() => updateQuantity(item.slug, item.quantity - 1)} data-testid={`cart-decrease-button-${item.slug}`}>
                      <Minus aria-hidden="true" />
                    </Button>
                    <span data-testid={`cart-quantity-${item.slug}`}>{item.quantity}</span>
                    <Button variant="outline" size="icon" onClick={() => updateQuantity(item.slug, item.quantity + 1)} data-testid={`cart-increase-button-${item.slug}`}>
                      <Plus aria-hidden="true" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeFromCart(item.slug)} data-testid={`cart-remove-button-${item.slug}`}>
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-summary" data-testid="cart-summary">
              <span data-testid="cart-item-count">{itemCount} items</span>
              <strong data-testid="cart-total">₹{total.toLocaleString("en-IN")}</strong>
            </div>
            {!order ? (
              <form className="checkout-form" onSubmit={submitOrder} data-testid="checkout-form">
                <label data-testid="checkout-name-label">Name<Input value={checkout.customer_name} onChange={(e) => updateCheckout("customer_name", e.target.value)} required data-testid="checkout-name-input" /></label>
                <label data-testid="checkout-email-label">Email<Input type="email" value={checkout.email} onChange={(e) => updateCheckout("email", e.target.value)} required data-testid="checkout-email-input" /></label>
                <label data-testid="checkout-phone-label">WhatsApp Number<Input value={checkout.phone} onChange={(e) => updateCheckout("phone", e.target.value)} required data-testid="checkout-phone-input" /></label>
                <label data-testid="checkout-city-label">City<Input value={checkout.city} onChange={(e) => updateCheckout("city", e.target.value)} required data-testid="checkout-city-input" /></label>
                <label data-testid="checkout-message-label">Message<Textarea value={checkout.message} onChange={(e) => updateCheckout("message", e.target.value)} data-testid="checkout-message-input" /></label>
                <Button className="btn-primary" type="submit" disabled={loading} data-testid="checkout-submit-button">
                  {loading ? "Creating order" : "Create Order"}
                </Button>
                {error && <p className="form-status form-status--error" data-testid="checkout-error-message">{error}</p>}
              </form>
            ) : (
              <div className="order-confirmation" data-testid="order-confirmation">
                <h3 data-testid="order-confirmation-title">Order request created</h3>
                <p data-testid="order-confirmation-id">Reference {order.id.slice(0, 8).toUpperCase()}</p>
                <p data-testid="order-confirmation-copy">Send it on WhatsApp to confirm availability, shipping and payment details.</p>
                <a className="btn-primary btn-link" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="order-whatsapp-confirmation-link">
                  <MessageCircle aria-hidden="true" /> Confirm on WhatsApp
                </a>
                <Button className="btn-quiet" variant="outline" onClick={() => { clearCart(); setOrder(null); onClose(); }} data-testid="order-finish-button">
                  Finish
                </Button>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
